/* Reviews marquee, ported from BoxerBriefReviewsMarquee.tsx.
   Data = assets/s1-reviews.json (same 200 curated reviews the SPA ships).
   The JSON is fetched lazily when the section nears the viewport; shuffle,
   category filter, snap carousel and the detail modal match the SPA. */

const CATEGORY_MAP = {
  All: 'All',
  chafe: 'Anti-Chafe',
  comfort: 'Comfort',
  durable: 'Durability',
  gym: 'Gym & Sports',
  size: 'Sizing & Fit',
  value: 'Value & Packs',
};

const STAR_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="#fff" aria-hidden="true"><path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"/></svg>';

const escapeHtml = (s) => s.replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

const starRow = (rating, boxClass) => {
  let html = '';
  for (let i = 0; i < 5; i += 1) {
    html += `<span class="${boxClass}${i < rating ? ' is-filled' : ''}">${STAR_SVG}</span>`;
  }
  return html;
};

class S1Reviews extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    this.reviews = [];
    this.activeFilter = 'All';

    this.strip = this.querySelector('[data-s1-rev-strip]');
    this.filtersEl = this.querySelector('[data-s1-rev-filters]');
    this.modal = this.querySelector('[data-s1-rev-modal]');

    /* lazy fetch: load the JSON once the section approaches the viewport,
       with an idle-time fallback so non-scrolling agents (crawlers, users
       who never reach the section) still get content rendered */
    let loaded = false;
    const load = () => {
      if (loaded) return;
      loaded = true;
      this.loadReviews();
    };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          load();
        }
      }, { rootMargin: '400px' });
      io.observe(this);
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => load(), { timeout: 8000 });
      } else {
        setTimeout(load, 8000);
      }
    } else {
      load();
    }

    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-s1-rev-prev]')) return this.scrollStrip(-1);
      if (e.target.closest('[data-s1-rev-next]')) return this.scrollStrip(1);

      const pill = e.target.closest('[data-s1-rev-filter]');
      if (pill) {
        this.activeFilter = pill.dataset.s1RevFilter;
        if (this.strip) this.strip.scrollLeft = 0;
        this.renderFilters();
        return this.renderCards();
      }

      const card = e.target.closest('[data-s1-rev-card]');
      if (card) return this.openModal(parseInt(card.dataset.s1RevCard, 10));

      if (e.target.closest('[data-s1-rev-modal-close]')) return this.closeModal();
    });

    /* keyboard path: cards are role=button tabindex=0 */
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const card = e.target.closest('[data-s1-rev-card]');
      if (card) {
        e.preventDefault();
        this.openModal(parseInt(card.dataset.s1RevCard, 10));
      }
    });
  }

  async loadReviews() {
    try {
      const res = await fetch(this.dataset.reviewsUrl);
      if (!res.ok) throw new Error(`reviews fetch ${res.status}`);
      const data = await res.json();
      /* shuffle on load, same as the SPA's mount-time sort */
      this.reviews = data.sort(() => Math.random() - 0.5);
      this.renderFilters();
      this.renderCards();
    } catch (err) {
      console.error('s1-reviews: failed to load reviews', err);
      const loading = this.querySelector('[data-s1-rev-loading]');
      if (loading) loading.remove();
    }
  }

  get filtered() {
    return this.activeFilter === 'All'
      ? this.reviews
      : this.reviews.filter((r) => r.category === this.activeFilter);
  }

  renderFilters() {
    if (!this.filtersEl) return;
    const cats = ['All', ...new Set(this.reviews.map((r) => r.category).filter((c) => CATEGORY_MAP[c]))];
    /* datasets without categories (socks) get no filter row at all */
    if (cats.length <= 1) {
      this.filtersEl.hidden = true;
      return;
    }
    this.filtersEl.hidden = false;
    this.filtersEl.innerHTML = cats.map((cat) => `
      <button type="button" class="s1-rev__filter${cat === this.activeFilter ? ' is-active' : ''}" data-s1-rev-filter="${escapeHtml(cat)}">
        ${escapeHtml(CATEGORY_MAP[cat] || cat)}
      </button>`).join('');
  }

  renderCards() {
    if (!this.strip) return;
    const reviews = this.filtered;
    if (!reviews.length) {
      this.strip.innerHTML = '<div class="s1-rev__empty">No reviews found for this category.</div>';
      return;
    }
    this.strip.innerHTML = reviews.map((review, i) => {
      const isLong = review.body.length > 120;
      const body = isLong ? `${review.body.substring(0, 120)}...` : review.body;
      return `
        <div class="s1-rev__card" data-s1-rev-card="${i}" tabindex="0" role="button">
          <span class="sr-only">Read review: </span>
          <div class="s1-rev__card-stars">${starRow(review.rating, 's1-rev__star-sm')}</div>
          <p class="s1-rev__card-title">${escapeHtml(review.title)}</p>
          <p class="s1-rev__card-body">${escapeHtml(body)}${isLong ? ' <span class="s1-rev__read-more">Read more</span>' : ''}</p>
          <div class="s1-rev__card-footer"><span>${escapeHtml(review.name.trim())}</span></div>
        </div>`;
    }).join('');
  }

  scrollStrip(dir) {
    if (this.strip) this.strip.scrollBy({ left: dir * 280, behavior: 'smooth' });
  }

  openModal(index) {
    const review = this.filtered[index];
    if (!review || !this.modal) return;
    this.modal.querySelector('[data-s1-rev-modal-stars]').innerHTML = starRow(review.rating, 's1-rev__star-box');
    this.modal.querySelector('[data-s1-rev-modal-title]').textContent = review.title;
    this.modal.querySelector('[data-s1-rev-modal-body]').textContent = `"${review.body}"`;
    this.modal.querySelector('[data-s1-rev-modal-name]').textContent = review.name;
    this.modal.querySelector('[data-s1-rev-modal-category]').textContent =
      review.category ? `${CATEGORY_MAP[review.category] || review.category} Customer` : 'Verified Customer';
    this.modal.hidden = false;
    document.body.style.overflow = 'hidden';
    this._prevFocus = document.activeElement;
    const closeBtn = this.modal.querySelector('[data-s1-rev-modal-close][aria-label]') || this.modal.querySelector('.s1-rev__modal-x');
    if (closeBtn) closeBtn.focus();
    this._onKeydown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.closeModal();
      }
    };
    document.addEventListener('keydown', this._onKeydown);
  }

  closeModal() {
    if (!this.modal || this.modal.hidden) return;
    this.modal.hidden = true;
    document.body.style.overflow = '';
    if (this._onKeydown) {
      document.removeEventListener('keydown', this._onKeydown);
      this._onKeydown = null;
    }
    if (this._prevFocus && this._prevFocus.isConnected) this._prevFocus.focus();
  }
}

if (!customElements.get('s1-reviews')) customElements.define('s1-reviews', S1Reviews);
